/**
 * TvPillarBar — slim contextual breadcrumb shown above pillar pages.
 *
 * Renders once per Triveny page just above the page body:
 *
 *   ✸  DESIGN · Studio
 *   ✸  MARKET · Campaigns
 *   ✸  ENGAGE · Pipeline
 *   ✸  OPERATE · RevOps
 *
 * The eyebrow style mirrors the home page's hero-eyebrow treatment
 * (monospace uppercase + multi-color spark) so users get the same
 * pillar context whether they're on the home launcher or deep inside
 * Studio / Pipeline / etc.
 *
 * Wire-up: a single component is rendered in app.tsx wrapping every
 * Triveny activeTab branch. To revert, delete this file + its import
 * + the wrapper invocations in app.tsx (search for "TvPillarBar").
 */

import { useState, type CSSProperties, type ReactNode } from 'react';
import { User, LogOut } from 'lucide-react';
import { tvTokens } from '@/src/platform/shared/components/triveny';

export type TvPillar = 'Design' | 'Market' | 'Engage' | 'Operate';

interface PillarMeta {
  pillar: TvPillar;
  pageLabel: string;
}

// activeTab → pillar + page-label map. Mirrors the rail groupings in
// TrivenyRail.tsx (RAIL_GROUPS). Add entries here when you add a new
// pillar page; unmapped tabs render no bar.
const PILLAR_BY_TAB: Record<string, PillarMeta> = {
  // Design
  'platform-web-presence': { pillar: 'Design', pageLabel: 'Studio' },
  // Market
  'platform-marketing': { pillar: 'Market', pageLabel: 'Campaigns' },
  'platform-contacts': { pillar: 'Market', pageLabel: 'Audiences' },
  'platform-contacts-view': { pillar: 'Market', pageLabel: 'Audiences' },
  'platform-contacts-new': { pillar: 'Market', pageLabel: 'Audiences' },
  'platform-contacts-edit': { pillar: 'Market', pageLabel: 'Audiences' },
  'platform-outreach': { pillar: 'Market', pageLabel: 'Funnel' },
  'platform-visitor-id': { pillar: 'Market', pageLabel: 'Funnel · Visitor ID' },
  'platform-lead-builder': { pillar: 'Market', pageLabel: 'Funnel · Lead Builder' },
  'platform-cold-outreach': { pillar: 'Market', pageLabel: 'Funnel · Cold Sequences' },
  'platform-marketing-analytics': { pillar: 'Market', pageLabel: 'Analytics' },
  // Engage
  'platform-sales': { pillar: 'Engage', pageLabel: 'Pipeline' },
  'platform-intelligence': { pillar: 'Engage', pageLabel: 'Intelligence' },
  // Operate
  'platform-revops': { pillar: 'Operate', pageLabel: 'RevOps' },
  'platform-agents': { pillar: 'Operate', pageLabel: 'Agents' },
  'platform-support': { pillar: 'Operate', pageLabel: 'Support' },
  'platform-invoices': { pillar: 'Operate', pageLabel: 'Invoices' },
};

// Per-pillar accent color matches the brand spark palette (teal / blue
// / violet) plus a neutral for Operate.
const PILLAR_ACCENT: Record<TvPillar, string> = {
  Design: '#009a83',
  Market: '#46b1e5',
  Engage: '#7468ae',
  Operate: '#64748b',
};

/** activeTab → pillar lookup, exported so callers (e.g. app.tsx restoring the
 *  home launcher's active pillar) can derive the owning pillar of any tab. */
export function pillarForTab(tab: string): TvPillar | null {
  return PILLAR_BY_TAB[tab]?.pillar ?? null;
}

function initialsOf(name?: string | null): string {
  const n = (name || '').trim();
  if (!n) return '?';
  const parts = n.split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || n[0].toUpperCase();
}

interface Props {
  activeTab: string;
  /** Optional override — when an inner sub-page wants to set its own label. */
  pageLabel?: string;
  /** User display name for the top-right avatar (initials + menu). */
  userName?: string | null;
  /** Open the profile/account drawer. */
  onShowProfile?: () => void;
  /** Sign out. */
  onLogout?: () => void;
  /** Shell controls shown at the right, before the avatar (e.g. "Open in…"). */
  actions?: ReactNode;
}

/**
 * Slim pillar breadcrumb on the left + a top-right user menu (initials +
 * dropdown) that mirrors the home page's avatar. Renders nothing only when
 * there's neither a pillar breadcrumb nor a user to show. (Never rendered on
 * the full-bleed home launcher, which has its own avatar.)
 */
export default function TvPillarBar({ activeTab, pageLabel, userName, onShowProfile, onLogout, actions }: Props) {
  const [menuOpen, setMenuOpen] = useState(false);
  const meta = PILLAR_BY_TAB[activeTab];
  if (!meta && !userName) return null;
  const label = meta ? (pageLabel ?? meta.pageLabel) : null;
  const accent = meta ? PILLAR_ACCENT[meta.pillar] : tvTokens.ink500;

  const menuItemStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 10px',
    borderRadius: tvTokens.r,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontFamily: tvTokens.fontSans,
    fontSize: 13,
    textTransform: 'none',
    letterSpacing: 'normal',
    color: tvTokens.ink700,
    textAlign: 'left',
    width: '100%',
  };

  return (
    <div
      role="navigation"
      aria-label={meta ? `${meta.pillar} pillar` : 'Top bar'}
      style={{
        // Slim bar — 36px tall — sits inside .triveny-platform pages
        // above the page content. Lives at the top of the scroll
        // container so it never gets covered by sticky page chrome.
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        height: 36,
        padding: '0 24px',
        borderBottom: `1px solid ${tvTokens.border}`,
        background: tvTokens.paper,
        fontFamily: tvTokens.fontMono,
        fontSize: 11,
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.14em',
        color: tvTokens.ink500,
      }}
    >
      {meta && (
        <>
          {/* Triveny spark mark — same favicon SVG used in the home hero
              eyebrow and in the browser tab. */}
          <span
            aria-hidden="true"
            style={{
              width: 14,
              height: 14,
              flexShrink: 0,
              backgroundImage: 'url(/triveny/favicon.svg)',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              backgroundSize: 'contain',
            }}
          />
          {/* Pillar name in accent color. */}
          <span style={{ color: accent, fontWeight: 600 }}>{meta.pillar}</span>
          {/* Separator dot. */}
          <span aria-hidden="true" style={{ color: tvTokens.ink500 }}>·</span>
          {/* Page label, ink + slightly looser tracking vs the pillar. */}
          <span style={{ color: tvTokens.ink, letterSpacing: '0.08em' }}>{label}</span>
        </>
      )}

      {/* Top-right user menu — initials avatar + dropdown (View profile /
          Sign out). Mirrors the home page's avatar so the account controls
          live in the same place across the launcher and the pillar pages. */}
      {actions && (
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', textTransform: 'none', letterSpacing: 'normal' }}>
          {actions}
        </div>
      )}
      {userName && (
        <div style={{ marginLeft: actions ? 0 : 'auto', position: 'relative' }}>
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label={`${userName} menu`}
            title={`${userName} — open menu`}
            style={{
              width: 26,
              height: 26,
              borderRadius: 999,
              background: tvTokens.accent,
              color: '#fff',
              fontFamily: tvTokens.fontDisplay,
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: 'normal',
              textTransform: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            {initialsOf(userName)}
          </button>
          {menuOpen && (
            <div
              role="menu"
              onMouseLeave={() => setMenuOpen(false)}
              style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                right: 0,
                zIndex: 60,
                minWidth: 184,
                background: tvTokens.surface,
                border: `1px solid ${tvTokens.border}`,
                borderRadius: tvTokens.rLg,
                boxShadow: tvTokens.shadow2,
                padding: 6,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                textTransform: 'none',
                letterSpacing: 'normal',
              }}
            >
              <div
                style={{
                  fontFamily: tvTokens.fontMono,
                  fontSize: 10,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: tvTokens.ink500,
                  padding: '6px 10px 4px',
                }}
              >
                {userName}
              </div>
              <button
                type="button"
                role="menuitem"
                style={menuItemStyle}
                onClick={() => {
                  setMenuOpen(false);
                  onShowProfile?.();
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = tvTokens.paper2;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'none';
                }}
              >
                <User size={14} /> View profile
              </button>
              {onLogout && (
                <button
                  type="button"
                  role="menuitem"
                  style={menuItemStyle}
                  onClick={() => {
                    setMenuOpen(false);
                    onLogout();
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = tvTokens.paper2;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'none';
                  }}
                >
                  <LogOut size={14} /> Sign out
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
